import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { toast } from 'sonner';
import { 
  LayoutGrid, 
  MessageSquare, 
  Hash,
  Lock,
  Palette,
  ChevronDown,
  ChevronRight,
  Plus,
  Search,
  Bell,
  Grid3X3,
  Users,
  MessageCircle,
  ChevronUp,
  FolderKanban,
  Settings,
  Pencil,
  Shield,
  Trash2,
  Circle,
  LogOut,
  User,
  Video,
  Building2,
  UserPlus,
  Wifi,
  WifiOff
} from 'lucide-react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { cn } from '../../lib/utils';

// Import modals from new components
import {
  CreateChannelModal,
  CreateHubModal,
  EditHubModal,
  ManageMembersModal,
  BoardPermissionsModal,
  DeleteHubModal,
  EditChannelModal,
  ManageChannelMembersModal,
  DeleteChannelModal,
  StartDMModal,
  CreateGroupModal,
  AssignHubModal
} from './modals';

// Import workspace components
import { WorkspaceSwitcher, CreateWorkspaceModal, InviteToWorkspaceModal } from '../workspace';

// Import notifications
import { NotificationsDropdown } from '../notifications';

// Import hooks
import { useHubWebSocket } from '../../hooks/useHubWebSocket';

// Import Zustand stores
import { useSidebarState, useWorkspaceHubsCache } from '../../stores';
import { BACKEND_URL } from '@/config/api';

// Hub colors - default palette
const hubColors = ['#F97316', '#3B82F6', '#10B981', '#8B5CF6', '#EF4444', '#06B6D4', '#EC4899'];
const getHubColor = (index) => hubColors[index % hubColors.length];

// Apply workspace brand color as CSS variable
const applyBrandColor = (brandColor) => {
  if (brandColor) {
    document.documentElement.style.setProperty('--primary', brandColor);
  } else {
    // Reset to default
    document.documentElement.style.removeProperty('--primary');
  }
};

export const AppSidebar = ({
  selectedChannel,
  onSelectChannel,
  onChannelsLoaded,
  mobileOpen = false,
  onClose,
}) => {
  const { user, token, logout } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const location = useLocation();
  
  // Zustand sidebar state
  const { 
    expandedHubId, 
    setExpandedHub,
    expandedSections,
    toggleSection: zustandToggleSection,
    userStatus,
    setUserStatus: zustandSetUserStatus
  } = useSidebarState();
  
  // Zustand cache store
  const {
    workspaces,
    currentWorkspace,
    setCurrentWorkspace,
    fetchWorkspaces,
    hubsByWorkspace,
    fetchHubs,
    addHubToCache,
    removeHubFromCache,
    updateHubsCache,
    clearCache,
    hubContentCache,
    fetchHubContent,
    prefetchHubContent,
    invalidateHubContent,
    dmConversations,
    fetchDMConversations,
    invitedHubs,
    hubAssignments,
    fetchInvitedHubs,
    assignHubToWorkspace,
    removeHubAssignment,
    getUnassignedHubs
  } = useWorkspaceHubsCache();
  
  // Get hubs for current workspace from cache
  const ownedHubs = currentWorkspace ? (hubsByWorkspace[currentWorkspace.id] || []) : [];
  
  // Get invited hubs assigned to current workspace
  // Note: The backend now includes assigned invited hubs in the /api/hubs?workspace_id response
  // So we only need to add invited hubs that are NOT already in ownedHubs
  const ownedHubIds = new Set(ownedHubs.map(h => h.id));
  const assignedInvitedHubs = currentWorkspace 
    ? invitedHubs.filter(h => hubAssignments[h.id] === currentWorkspace.id && !ownedHubIds.has(h.id))
    : [];
  
  // Combined hubs for display
  const hubs = [...ownedHubs, ...assignedInvitedHubs];
  
  // Unassigned invited hubs
  const unassignedHubs = getUnassignedHubs();
  
  // Local state for modals and UI
  const [isCreateWorkspaceOpen, setIsCreateWorkspaceOpen] = useState(false);
  const [isInviteToWorkspaceOpen, setIsInviteToWorkspaceOpen] = useState(false);
  const [isAssignHubOpen, setIsAssignHubOpen] = useState(false);
  const [hubToAssign, setHubToAssign] = useState(null);
  
  // Hub content from cache
  const hubContent = expandedHubId ? hubContentCache[expandedHubId] : null;
  const channels = hubContent?.channels || [];
  const boards = hubContent?.boards || [];
  const groups = hubContent?.groups || [];
  
  // Derived state for backwards compatibility
  const selectedHub = expandedHubId 
    ? hubs.find(h => h.id === expandedHubId) || { id: 'default', name: currentWorkspace?.name || 'My Workspace', color: '#F97316' }
    : { id: 'default', name: currentWorkspace?.name || 'My Workspace', color: '#F97316' };

  // Modal states
  const [isCreateChannelOpen, setIsCreateChannelOpen] = useState(false);
  const [isCreateHubOpen, setIsCreateHubOpen] = useState(false);
  const [isStartDMOpen, setIsStartDMOpen] = useState(false);
  const [isCreateGroupOpen, setIsCreateGroupOpen] = useState(false);
  
  // Hub modal states
  const [editingHub, setEditingHub] = useState(null);
  const [isEditHubOpen, setIsEditHubOpen] = useState(false);
  const [isManageMembersOpen, setIsManageMembersOpen] = useState(false);
  const [isBoardPermissionsOpen, setIsBoardPermissionsOpen] = useState(false);
  const [isDeleteHubOpen, setIsDeleteHubOpen] = useState(false);
  const [hubMembers, setHubMembers] = useState([]);
  const [hubBoards, setHubBoards] = useState([]);

  // Channel modal states
  const [editingChannel, setEditingChannel] = useState(null);
  const [isEditChannelOpen, setIsEditChannelOpen] = useState(false);
  const [isManageChannelMembersOpen, setIsManageChannelMembersOpen] = useState(false);
  const [isDeleteChannelOpen, setIsDeleteChannelOpen] = useState(false);

  // Initial data fetch using Zustand stores
  useEffect(() => {
    if (token) {
      fetchWorkspaces(token);
      fetchDMConversations(token);
      fetchInvitedHubs(token);  // Fetch invited hubs
    }
  }, [token, fetchWorkspaces, fetchDMConversations, fetchInvitedHubs]);

  // Fetch hubs when workspace changes - force refresh to get latest data
  useEffect(() => {
    if (token && currentWorkspace) {
      // Force refresh on workspace change to ensure we have the latest hubs
      fetchHubs(token, currentWorkspace.id, true);
      // Apply workspace brand color
      applyBrandColor(currentWorkspace.brand_color);
    }
  }, [token, currentWorkspace, fetchHubs]);

  // Fetch hub content when hub expands
  useEffect(() => {
    if (token && expandedHubId) {
      fetchHubContent(token, expandedHubId).then(content => {
        if (content && onChannelsLoaded) {
          onChannelsLoaded(content.channels);
        }
        // Auto-select first channel if on messages page
        if (content?.channels?.length > 0 && location.pathname === '/messages' && !selectedChannel) {
          onSelectChannel?.(content.channels[0]);
        }
      });
    }
  }, [token, expandedHubId, fetchHubContent, onChannelsLoaded, location.pathname, selectedChannel, onSelectChannel]);

  // Real-time hub updates via WebSocket
  const { isConnected: hubWsConnected } = useHubWebSocket(
    expandedHubId,
    token,
    {
      showToasts: true,
      onHubUpdated: (updatedHub) => {
        // Refresh hub data
        if (currentWorkspace) {
          fetchHubs(token, currentWorkspace.id, true);
        }
      },
      onMemberJoined: (member) => {
        // Refresh hub content to show new member
        if (expandedHubId) {
          fetchHubContent(token, expandedHubId);
        }
      },
      onMemberLeft: (userId) => {
        // Refresh hub content
        if (expandedHubId) {
          fetchHubContent(token, expandedHubId);
        }
      },
      onHubDeleted: (hubId) => {
        // Refresh hubs list and clear expansion if deleted hub was expanded
        if (currentWorkspace) {
          fetchHubs(token, currentWorkspace.id, true);
        }
        if (expandedHubId === hubId) {
          setExpandedHub(null);
          navigate('/dashboard');
        }
      },
      onChannelCreated: (channel) => {
        // Refresh hub content to show new channel
        if (expandedHubId) {
          invalidateHubContent(expandedHubId);
          fetchHubContent(token, expandedHubId);
        }
      },
      onChannelDeleted: (channelId) => {
        // Refresh hub content
        if (expandedHubId) {
          invalidateHubContent(expandedHubId);
          fetchHubContent(token, expandedHubId);
        }
      },
      onBoardCreated: (board) => {
        // Refresh hub content
        if (expandedHubId) {
          invalidateHubContent(expandedHubId);
          fetchHubContent(token, expandedHubId);
        }
      },
      onBoardDeleted: (boardId) => {
        // Refresh hub content
        if (expandedHubId) {
          invalidateHubContent(expandedHubId);
          fetchHubContent(token, expandedHubId);
        }
      }
    }
  );

  // Section toggle (using Zustand)
  const toggleSection = (section) => {
    zustandToggleSection(section);
  };

  // Hub expansion handler with prefetching
  const toggleHubExpanded = (hubId) => {
    setExpandedHub(hubId);
  };
  
  // Prefetch hub content on hover
  const handleHubHover = (hubId) => {
    if (token && hubId !== expandedHubId) {
      prefetchHubContent(token, hubId);
    }
  };

  // Backwards compat - expandedHubs as array
  const expandedHubs = expandedHubId ? [expandedHubId] : [];

  // Hub handlers
  const openEditHub = (hub, index) => {
    setEditingHub({ ...hub, color: getHubColor(index), index });
    setIsEditHubOpen(true);
  };

  const openManageMembers = async (hub, index) => {
    setEditingHub({ ...hub, color: getHubColor(index), index });
    setHubMembers([
      { id: user.id, name: user.name, email: user.email, role: 'admin', avatar_color: user.avatar_color },
    ]);
    setIsManageMembersOpen(true);
  };

  const openBoardPermissions = async (hub, index) => {
    setEditingHub({ ...hub, color: getHubColor(index), index });
    const filtered = boards.filter(b => b.hub_id === hub.id || !b.hub_id);
    setHubBoards(filtered.map(b => ({ ...b, permissions: { canView: true, canEdit: true } })));
    setIsBoardPermissionsOpen(true);
  };

  const openDeleteHub = (hub, index) => {
    setEditingHub({ ...hub, color: getHubColor(index), index });
    setIsDeleteHubOpen(true);
  };

  const handleUpdateHub = async (hubId, newName) => {
    // Update hub in cache
    if (currentWorkspace) {
      const updatedHubs = hubs.map(h => h.id === hubId ? { ...h, name: newName } : h);
      updateHubsCache(currentWorkspace.id, updatedHubs);
    }
  };

  const handleDeleteHub = async (hubId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/hubs/${hubId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to delete hub');
      }
      
      // Update cache
      if (currentWorkspace) {
        removeHubFromCache(currentWorkspace.id, hubId);
      }
      
      // If the deleted hub was expanded, switch to another hub
      if (expandedHubId === hubId) {
        const remaining = hubs.filter(h => h.id !== hubId);
        setExpandedHub(remaining.length > 0 ? remaining[0].id : null);
      }
      
      toast.success('Hub deleted successfully');
    } catch (error) {
      console.error('Error deleting hub:', error);
      toast.error(error.message || 'Failed to delete hub');
      throw error;
    }
  };

  const handleInviteMembers = (emails, role) => {
    const emailList = emails.split(',').map(e => e.trim()).filter(e => e);
    setHubMembers(prev => [
      ...prev,
      ...emailList.map((email, i) => ({
        id: `pending-${i}`,
        email,
        name: email.split('@')[0],
        role,
        status: 'pending',
        avatar_color: hubColors[(prev.length + i) % hubColors.length]
      }))
    ]);
  };

  // Channel handlers
  const openEditChannel = (channel) => {
    setEditingChannel(channel);
    setIsEditChannelOpen(true);
  };

  const openDeleteChannel = (channel) => {
    setEditingChannel(channel);
    setIsDeleteChannelOpen(true);
  };

  const handleChannelCreated = (newChannel) => {
    // Invalidate hub content cache to refetch
    if (expandedHubId) {
      invalidateHubContent(expandedHubId);
      fetchHubContent(token, expandedHubId);
    }
    onSelectChannel?.(newChannel);
  };

  const handleChannelUpdated = (channelId, name, description) => {
    // Invalidate and refetch hub content
    if (expandedHubId) {
      invalidateHubContent(expandedHubId);
      fetchHubContent(token, expandedHubId);
    }
  };

  const handleChannelDeleted = (channelId) => {
    // Invalidate and refetch hub content
    if (expandedHubId) {
      invalidateHubContent(expandedHubId);
      fetchHubContent(token, expandedHubId);
    }
    if (selectedChannel?.id === channelId) {
      onSelectChannel?.(null);
    }
  };

  // Hub created handler
  const handleHubCreated = (newHub) => {
    // Add to cache
    if (currentWorkspace) {
      addHubToCache(currentWorkspace.id, newHub);
    }
    setExpandedHub(newHub.id);  // Auto-expand new hub
  };

  // DM handler
  const handleDMStarted = (conversation) => {
    fetchDMConversations(token);
    // Navigate to the new DM conversation
    navigate(`/dm/${conversation.id}`);
  };

  const handleLogout = () => {
    // Clear cache on logout
    clearCache();
    logout();
    navigate('/');
  };

  const isChannelActive = (channelId) => 
    location.pathname === '/messages' && selectedChannel?.id === channelId;

  // Filter hubs - separate owned from assigned invited
  const displayOwnedHubs = hubs.filter(h => h.owner_id === user?.id);
  const displayInvitedHubs = hubs.filter(h => h.owner_id !== user?.id);

  // Open assign hub modal
  const openAssignHubModal = (hub) => {
    setHubToAssign(hub);
    setIsAssignHubOpen(true);
  };

  // Check if user is admin
  const isAdmin = Boolean(
    user?.is_admin === true ||
    (user?.role != null && String(user.role).trim().toLowerCase() === 'admin')
  );

  // Navigation items definition - Dashboard, Messages (DMs), and Meetings are now inside hubs
  const navItems = [
    { id: 'dashboard', icon: Grid3X3, label: 'Dashboard', link: '/dashboard' },
    { id: 'boards', icon: FolderKanban, label: 'Boards', link: '/projects' },
    { id: 'channels', icon: MessageSquare, label: 'Channels', items: channels, hasAdd: true },
    { id: 'direct-messages', icon: MessageCircle, label: 'Messages', items: dmConversations, hasAdd: true, isDM: true },
    { id: 'groups', icon: Users, label: 'Groups', items: groups, hasAdd: true, isGroup: true },
    { id: 'whiteboards', icon: Palette, label: 'Canvas', items: boards, hasAdd: true, isCanvas: true },
    { id: 'meetings', icon: Video, label: 'Meetings', link: '/meetings' },
    ...(isAdmin ? [{ id: 'admin', icon: Shield, label: 'Admin', link: '/admin' }] : []),
  ];

  // Render hub item in sidebar
  const renderHubNavItem = (item, hubId) => {
    const isExpanded = expandedSections.includes(item.id);
    const Icon = item.icon;

    if (item.link) {
      const isActive = item.id === 'meetings' 
        ? (location.pathname === '/meetings' || location.pathname.startsWith('/meeting/'))
        : item.id === 'admin'
        ? location.pathname === '/admin'
        : location.pathname === item.link;
      
      return (
        <Link
          key={item.id}
          to={item.link}
          className={cn(
            "flex items-center gap-2 px-3 py-1.5 rounded text-sm transition-colors text-white/70 hover:text-white hover:bg-white/10",
            isActive && "bg-white/10 text-white"
          )}
          data-testid={`${item.id}-nav-link`}
        >
          <Icon size={16} />
          <span>{item.label}</span>
        </Link>
      );
    }

    return (
      <div key={item.id} className="space-y-0.5">
        <button
          onClick={() => toggleSection(item.id)}
          className="w-full flex items-center justify-between px-3 py-1.5 rounded text-sm text-white/70 hover:text-white hover:bg-white/10 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Icon size={16} />
            <span>{item.label}</span>
          </div>
          <div className="flex items-center gap-1">
            {item.hasAdd && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (item.id === 'channels') setIsCreateChannelOpen(true);
                  else if (item.id === 'direct-messages') setIsStartDMOpen(true);
                  else if (item.id === 'groups') setIsCreateGroupOpen(true);
                  else if (item.id === 'whiteboards') navigate('/whiteboard/new');
                }}
                className="p-0.5 hover:bg-white/20 rounded"
                data-testid={`add-${item.id}-btn`}
              >
                <Plus size={14} />
              </button>
            )}
            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </div>
        </button>

        {isExpanded && item.items && (
          <div className="ml-4 space-y-0.5">
            {item.items.length === 0 ? (
              <p className="text-xs text-white/40 px-3 py-1">No items</p>
            ) : (
              item.items.map((subItem) => {
                if (item.isDM) {
                  const otherUser = subItem.participants?.find(p => p.user_id !== user?.id);
                  return (
                    <Link
                      key={subItem.id}
                      to={`/dm/${subItem.id}`}
                      className={cn(
                        "flex items-center gap-2 px-3 py-1.5 rounded text-sm text-white/60 hover:text-white hover:bg-white/10",
                        location.pathname === `/dm/${subItem.id}` && "bg-white/10 text-white"
                      )}
                    >
                      <div 
                        className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-medium"
                        style={{ backgroundColor: otherUser?.avatar_color || '#6366F1' }}
                      >
                        {otherUser?.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <span className="truncate">{otherUser?.name || 'Unknown'}</span>
                      {subItem.unread_count > 0 && (
                        <span className="ml-auto bg-blue-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                          {subItem.unread_count}
                        </span>
                      )}
                    </Link>
                  );
                }

                if (item.isGroup) {
                  return (
                    <Link
                      key={subItem.id}
                      to={`/group/${subItem.id}`}
                      className={cn(
                        "flex items-center gap-2 px-3 py-1.5 rounded text-sm text-white/60 hover:text-white hover:bg-white/10",
                        location.pathname === `/group/${subItem.id}` && "bg-white/10 text-white"
                      )}
                    >
                      <div className="w-5 h-5 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                        <Users size={10} className="text-white" />
                      </div>
                      <span className="truncate">{subItem.name}</span>
                      {subItem.unread_count > 0 && (
                        <span className="ml-auto bg-blue-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                          {subItem.unread_count}
                        </span>
                      )}
                    </Link>
                  );
                }

                if (item.isCanvas) {
                  return (
                    <Link
                      key={subItem.id}
                      to={`/whiteboard/${subItem.id}`}
                      className={cn(
                        "flex items-center gap-2 px-3 py-1.5 rounded text-sm text-white/60 hover:text-white hover:bg-white/10",
                        location.pathname === `/whiteboard/${subItem.id}` && "bg-white/10 text-white"
                      )}
                    >
                      <Palette size={14} className="text-blue-400" />
                      <span className="truncate">{subItem.name || 'Untitled'}</span>
                    </Link>
                  );
                }

                // Channels
                return (
                  <div key={subItem.id} className="group flex items-center">
                    <button
                      onClick={() => {
                        onSelectChannel?.(subItem);
                        navigate('/messages');
                      }}
                      className={cn(
                        "flex-1 flex items-center gap-2 px-3 py-1.5 rounded-l text-sm text-white/60 hover:text-white hover:bg-white/10 transition-colors",
                        isChannelActive(subItem.id) && "bg-white/10 text-white"
                      )}
                    >
                      {subItem.is_private ? <Lock size={14} /> : <Hash size={14} />}
                      <span className="truncate">{subItem.name}</span>
                    </button>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="p-1 opacity-0 group-hover:opacity-100 hover:bg-white/10 rounded transition-opacity">
                          <Settings size={12} className="text-white/50" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-[#1a1d21] border-white/20">
                        <DropdownMenuItem 
                          className="text-gray-300 hover:text-white focus:bg-white/10 cursor-pointer"
                          onClick={() => openEditChannel(subItem)}
                        >
                          <Pencil size={14} className="mr-2" /> Edit Channel
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-white/10" />
                        <DropdownMenuItem 
                          className="text-red-400 hover:text-red-300 focus:bg-red-500/10 cursor-pointer"
                          onClick={() => openDeleteChannel(subItem)}
                        >
                          <Trash2 size={14} className="mr-2" /> Delete Channel
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    );
  };

  // Close mobile sidebar on navigation
  useEffect(() => {
    onClose?.();
  }, [location.pathname, onClose]);

  return (
    <div
      className={cn(
        "flex h-screen fixed left-0 top-0 z-40 transition-transform duration-200 ease-out",
        !mobileOpen && "max-md:-translate-x-full"
      )}
      data-testid="app-sidebar"
    >
      {/* Workspace Strip - Far left */}
      <div className="w-16 bg-[#0F2942] flex flex-col items-center py-4 border-r border-white/10">
        {/* Workspaces */}
        {workspaces.map((workspace, index) => (
          <div key={workspace.id} className="relative group mb-2">
            <div 
              className={cn(
                "w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold cursor-pointer transition-all hover:rounded-lg",
                currentWorkspace?.id === workspace.id && "ring-2 ring-white"
              )}
              style={{ backgroundColor: workspace.brand_color || ['#2563EB', '#7C3AED', '#059669', '#DC2626', '#D97706'][index % 5] }}
              onClick={() => setCurrentWorkspace(workspace)}
              title={workspace.name}
            >
              {workspace.name?.charAt(0).toUpperCase()}
            </div>
            <div className="absolute left-full ml-2 px-2 py-1 bg-black/90 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">
              {workspace.name}
            </div>
          </div>
        ))}
        
        <div className="flex-1" />
        
        {/* Add Workspace Button */}
        <button 
          className="w-10 h-10 rounded-xl border-2 border-dashed border-white/30 flex items-center justify-center text-white/50 hover:border-white/50 hover:text-white/70 transition-colors"
          onClick={() => setIsCreateWorkspaceOpen(true)}
          title="Create Workspace"
        >
          <Plus size={20} />
        </button>
      </div>

      {/* Main Sidebar */}
      <div className="w-72 bg-[#1E3A5F] flex flex-col">
        {/* Header - Shows Workspace name */}
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div 
                className="w-7 h-7 rounded flex items-center justify-center text-white text-sm font-bold"
                style={{ backgroundColor: currentWorkspace?.brand_color || '#2563EB' }}
              >
                {currentWorkspace?.name?.charAt(0).toUpperCase() || 'W'}
              </div>
              <h2 className="font-semibold text-white truncate max-w-[140px]">{currentWorkspace?.name || 'Workspace'}</h2>
            </div>
            <div className="flex items-center gap-1">
              {/* Settings/Invite Button */}
              {currentWorkspace && (
                <button 
                  onClick={() => setIsInviteToWorkspaceOpen(true)}
                  className="p-1.5 hover:bg-white/10 rounded text-white/70 hover:text-white transition-colors"
                  title="Invite People"
                  data-testid="invite-people-btn"
                >
                  <Settings size={18} />
                </button>
              )}
              {/* Notifications */}
              <NotificationsDropdown />
            </div>
          </div>
          
          {/* Search */}
          <div className="mt-3 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={16} />
            <input
              type="text"
              placeholder="Search..."
              className="w-full bg-white/10 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-white/40 focus:outline-none focus:border-white/30"
              data-testid="sidebar-search"
            />
          </div>
        </div>

        {/* Your Hubs Section */}
        <div className="p-3 border-b border-white/10">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-white/50 uppercase tracking-wider">Your Hubs</span>
            <button 
              onClick={() => setIsCreateHubOpen(true)}
              className="p-1 hover:bg-white/10 rounded text-white/50 hover:text-white transition-colors"
              data-testid="create-hub-btn"
            >
              <Plus size={14} />
            </button>
          </div>
          
          {displayOwnedHubs.length === 0 && displayInvitedHubs.length === 0 ? (
            <button
              onClick={() => setIsCreateHubOpen(true)}
              className="w-full py-2 px-3 text-sm text-white/50 hover:text-white hover:bg-white/10 rounded transition-colors text-left"
            >
              + Create your first hub
            </button>
          ) : (
            <div className="space-y-1">
              {displayOwnedHubs.map((hub, index) => {
                const isExpanded = expandedHubs.includes(hub.id);
                const hubColor = getHubColor(index);
                
                return (
                  <div key={hub.id}>
                    {/* Hub Header */}
                    <div className="flex items-center group">
                      <button
                        onClick={() => toggleHubExpanded(hub.id)}
                        onMouseEnter={() => handleHubHover(hub.id)}
                        className="flex-1 flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/10 transition-colors"
                      >
                        <div 
                          className="w-5 h-5 rounded flex items-center justify-center text-white text-[10px] font-bold"
                          style={{ backgroundColor: hubColor }}
                        >
                          {hub.name?.charAt(0).toUpperCase()}
                        </div>
                        <span className="text-sm text-white/80 truncate flex-1 text-left">{hub.name}</span>
                        {isExpanded ? <ChevronDown size={14} className="text-white/50" /> : <ChevronRight size={14} className="text-white/50" />}
                      </button>
                      
                      {/* Hub Settings Dropdown */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button 
                            className="p-1 opacity-0 group-hover:opacity-100 hover:bg-white/10 rounded transition-opacity"
                            data-testid={`hub-settings-${hub.id}`}
                          >
                            <Settings size={14} className="text-white/50" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-[#1a1d21] border-white/20 w-48">
                          <DropdownMenuItem 
                            className="text-gray-300 hover:text-white focus:bg-white/10 cursor-pointer"
                            onClick={() => openEditHub(hub, index)}
                          >
                            <Pencil size={14} className="mr-2" /> Edit Hub
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            className="text-gray-300 hover:text-white focus:bg-white/10 cursor-pointer"
                            onClick={() => openManageMembers(hub, index)}
                          >
                            <Users size={14} className="mr-2" /> Manage Members
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            className="text-gray-300 hover:text-white focus:bg-white/10 cursor-pointer"
                            onClick={() => openBoardPermissions(hub, index)}
                          >
                            <Shield size={14} className="mr-2" /> Board Permissions
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-white/10" />
                          <DropdownMenuItem 
                            className="text-red-400 hover:text-red-300 focus:bg-red-500/10 cursor-pointer"
                            onClick={() => openDeleteHub(hub, index)}
                            data-testid={`delete-hub-${hub.id}`}
                          >
                            <Trash2 size={14} className="mr-2" /> Delete Hub
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    
                    {/* Hub Contents (expanded) */}
                    {isExpanded && (
                      <div className="ml-3 mt-1 pl-3 border-l border-white/10 space-y-0.5">
                        {navItems.map(item => renderHubNavItem(item, hub.id))}
                      </div>
                    )}
                  </div>
                );
              })}
              
              {/* Invited hubs assigned to this workspace */}
              {displayInvitedHubs.map((hub, i) => {
                const isExpanded = expandedHubs.includes(hub.id);
                const hubColor = hub.color || getHubColor(displayOwnedHubs.length + i);
                
                return (
                  <div key={hub.id}>
                    {/* Invited Hub Header */}
                    <div className="flex items-center group">
                      <button
                        onClick={() => toggleHubExpanded(hub.id)}
                        onMouseEnter={() => handleHubHover(hub.id)}
                        className="flex-1 flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/10 transition-colors"
                      >
                        <div 
                          className="w-5 h-5 rounded flex items-center justify-center text-white text-[10px] font-bold"
                          style={{ backgroundColor: hubColor }}
                        >
                          {hub.name?.charAt(0).toUpperCase()}
                        </div>
                        <span className="text-sm text-white/80 truncate flex-1 text-left">{hub.name}</span>
                        <span className="text-[10px] text-white/40 px-1 py-0.5 bg-white/10 rounded">invited</span>
                        {isExpanded ? <ChevronDown size={14} className="text-white/50" /> : <ChevronRight size={14} className="text-white/50" />}
                      </button>
                      
                      {/* Organize button */}
                      <button 
                        onClick={() => openAssignHubModal(hub)}
                        className="p-1 opacity-0 group-hover:opacity-100 hover:bg-white/10 rounded transition-opacity"
                        title="Organize hub"
                      >
                        <FolderKanban size={14} className="text-white/50" />
                      </button>
                    </div>
                    
                    {/* Invited Hub Contents (expanded) */}
                    {isExpanded && (
                      <div className="ml-3 mt-1 pl-3 border-l border-white/10 space-y-0.5">
                        {navItems.map(item => renderHubNavItem(item, hub.id))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Unassigned Invited Hubs Section */}
        {unassignedHubs.length > 0 && (
          <div className="p-3 border-b border-white/10">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-white/50 uppercase tracking-wider">Invited Hubs</span>
            </div>
            <div className="space-y-1">
              {unassignedHubs.map((hub, i) => {
                const isExpanded = expandedHubs.includes(hub.id);
                const hubColor = hub.color || getHubColor(i);
                
                return (
                  <div key={hub.id}>
                    <div className="flex items-center group">
                      <button
                        onClick={() => toggleHubExpanded(hub.id)}
                        onMouseEnter={() => handleHubHover(hub.id)}
                        className="flex-1 flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/10 transition-colors"
                      >
                        <div 
                          className="w-5 h-5 rounded flex items-center justify-center text-white text-[10px] font-bold"
                          style={{ backgroundColor: hubColor }}
                        >
                          {hub.name?.charAt(0).toUpperCase()}
                        </div>
                        <span className="text-sm text-white/80 truncate flex-1 text-left">{hub.name}</span>
                        {isExpanded ? <ChevronDown size={14} className="text-white/50" /> : <ChevronRight size={14} className="text-white/50" />}
                      </button>
                      
                      {/* Assign to workspace button */}
                      <button 
                        onClick={() => openAssignHubModal(hub)}
                        className="p-1 opacity-0 group-hover:opacity-100 hover:bg-white/10 rounded transition-opacity text-white/50 hover:text-white"
                        title="Assign to workspace"
                      >
                        <FolderKanban size={14} />
                      </button>
                    </div>
                    
                    {/* Hub Contents (expanded) */}
                    {isExpanded && (
                      <div className="ml-3 mt-1 pl-3 border-l border-white/10 space-y-0.5">
                        {navItems.map(item => renderHubNavItem(item, hub.id))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* User Section */}
        <div className="p-3 border-t border-white/10">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div 
                className="w-9 h-9 rounded-full flex items-center justify-center text-white font-medium"
                style={{ backgroundColor: user?.avatar_color || '#6366F1' }}
              >
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <div className={cn(
                "absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-[#1E3A5F]",
                userStatus === 'online' ? "bg-green-500" : "bg-gray-500"
              )} />
            </div>
            
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-white/50 truncate">{user?.email}</p>
            </div>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="p-1.5 hover:bg-white/10 rounded transition-colors">
                  <ChevronUp size={16} className="text-white/70" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" side="top" className="bg-[#1a1d21] border-white/20 w-56 mb-2">
                <DropdownMenuItem 
                  className="text-gray-300 hover:text-white focus:bg-white/10 cursor-pointer"
                  onClick={() => navigate('/profile')}
                >
                  <User size={16} className="mr-3" /> Profile
                </DropdownMenuItem>
                <DropdownMenuItem 
                  className="text-gray-300 hover:text-white focus:bg-white/10 cursor-pointer"
                  onClick={() => navigate('/settings')}
                >
                  <Settings size={16} className="mr-3" /> Settings
                </DropdownMenuItem>
                {isAdmin && (
                  <>
                    <DropdownMenuSeparator className="bg-white/10" />
                    <div className="px-2 py-2">
                      <div className="flex items-center justify-between mb-2 px-2">
                        <p className="text-xs text-white/50">Admin Mode</p>
                        <Switch
                          checked={location.pathname === '/admin'}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              navigate('/admin');
                            } else {
                              navigate('/dashboard');
                            }
                          }}
                          className="data-[state=checked]:bg-purple-600"
                        />
                      </div>
                     
                    </div>
                  </>
                )}
                <DropdownMenuSeparator className="bg-white/10" />
                <div className="px-2 py-2">
                  <p className="text-xs text-white/50 mb-2 px-2">Status</p>
                  <button
                    onClick={() => zustandSetUserStatus('online')}
                    className={cn(
                      "w-full px-2 py-1.5 flex items-center gap-3 rounded transition-colors",
                      userStatus === 'online' ? "bg-white/10 text-white" : "text-gray-300 hover:bg-white/5"
                    )}
                  >
                    <Circle size={12} className="fill-green-500 text-green-500" />
                    <span className="text-sm">Online</span>
                  </button>
                  <button
                    onClick={() => zustandSetUserStatus('offline')}
                    className={cn(
                      "w-full px-2 py-1.5 flex items-center gap-3 rounded transition-colors",
                      userStatus === 'offline' ? "bg-white/10 text-white" : "text-gray-300 hover:bg-white/5"
                    )}
                  >
                    <Circle size={12} className="fill-gray-500 text-gray-500" />
                    <span className="text-sm">Offline</span>
                  </button>
                </div>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem 
                  className="text-red-400 hover:text-red-300 focus:bg-red-500/10 cursor-pointer"
                  onClick={handleLogout}
                >
                  <LogOut size={16} className="mr-3" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Modals */}
      <CreateChannelModal
        isOpen={isCreateChannelOpen}
        onClose={() => setIsCreateChannelOpen(false)}
        token={token}
        selectedHub={selectedHub}
        onChannelCreated={handleChannelCreated}
        onNavigate={navigate}
      />

      <CreateHubModal
        isOpen={isCreateHubOpen}
        onClose={() => setIsCreateHubOpen(false)}
        token={token}
        hubCount={hubs.length}
        workspaceId={currentWorkspace?.id}
        onHubCreated={handleHubCreated}
      />

      <StartDMModal
        isOpen={isStartDMOpen}
        onClose={() => setIsStartDMOpen(false)}
        token={token}
        onDMStarted={handleDMStarted}
      />

      <CreateGroupModal
        isOpen={isCreateGroupOpen}
        onClose={() => setIsCreateGroupOpen(false)}
        token={token}
        onGroupCreated={(group) => {
          // Invalidate and refetch hub content
          if (expandedHubId) {
            invalidateHubContent(expandedHubId);
            fetchHubContent(token, expandedHubId);
          }
          navigate(`/group/${group.id}`);
        }}
      />

      <InviteToWorkspaceModal
        isOpen={isInviteToWorkspaceOpen}
        onClose={() => setIsInviteToWorkspaceOpen(false)}
        token={token}
        workspace={currentWorkspace}
        hubs={hubs}
        onInvited={() => {
          fetchWorkspaces(token);
          if (currentWorkspace) {
            fetchHubs(token, currentWorkspace.id);
          }
        }}
      />

      <EditHubModal
        isOpen={isEditHubOpen}
        onClose={() => { setIsEditHubOpen(false); setEditingHub(null); }}
        hub={editingHub}
        token={token}
        onUpdate={handleUpdateHub}
      />

      <ManageMembersModal
        isOpen={isManageMembersOpen}
        onClose={() => { setIsManageMembersOpen(false); setEditingHub(null); }}
        hub={editingHub}
        members={hubMembers}
        currentUser={user}
        token={token}
        onInvite={handleInviteMembers}
        onRemoveMember={(memberId) => setHubMembers(prev => prev.filter(m => m.user_id !== memberId && m.id !== memberId))}
      />

      <BoardPermissionsModal
        isOpen={isBoardPermissionsOpen}
        onClose={() => { setIsBoardPermissionsOpen(false); setEditingHub(null); }}
        hub={editingHub}
        boards={hubBoards}
      />

      <DeleteHubModal
        isOpen={isDeleteHubOpen}
        onClose={() => { setIsDeleteHubOpen(false); setEditingHub(null); }}
        hub={editingHub}
        onDelete={handleDeleteHub}
      />

      <EditChannelModal
        isOpen={isEditChannelOpen}
        onClose={() => { setIsEditChannelOpen(false); setEditingChannel(null); }}
        channel={editingChannel}
        token={token}
        onUpdate={handleChannelUpdated}
      />

      <ManageChannelMembersModal
        isOpen={isManageChannelMembersOpen}
        onClose={() => { setIsManageChannelMembersOpen(false); setEditingChannel(null); }}
        channel={editingChannel}
      />

      <DeleteChannelModal
        isOpen={isDeleteChannelOpen}
        onClose={() => { setIsDeleteChannelOpen(false); setEditingChannel(null); }}
        channel={editingChannel}
        token={token}
        onDelete={handleChannelDeleted}
      />

      <AssignHubModal
        isOpen={isAssignHubOpen}
        onClose={() => { setIsAssignHubOpen(false); setHubToAssign(null); }}
        hub={hubToAssign}
        workspaces={workspaces}
        currentAssignment={hubToAssign ? hubAssignments[hubToAssign.id] : null}
        onAssign={(hubId, workspaceId) => assignHubToWorkspace(token, hubId, workspaceId)}
        onRemoveAssignment={(hubId) => removeHubAssignment(token, hubId)}
      />

      <CreateWorkspaceModal
        isOpen={isCreateWorkspaceOpen}
        onClose={() => setIsCreateWorkspaceOpen(false)}
        token={token}
        onCreated={(newWorkspace) => {
          fetchWorkspaces(token);
          setCurrentWorkspace(newWorkspace);
          setIsCreateWorkspaceOpen(false);
        }}
      />
    </div>
  );
};
